// @flow
import React from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import {
  Title,
  Heading,
  SubHeading,
  StyledP,
  Button,
  Flex,
  Divider,
  NewLogo,
  YellowPattern,
  GreenPattern,
  StyledAnchor
} from 'src/theme/globalStyle'
import { media } from 'src/theme/style-utils'
import { StyledFormPage, StyledFormInfo, StyledArticle, FieldCollection } from 'src/components/Form'
import { Tick } from 'src/components/Icons'
import { DetailTable } from 'src/components/DetailTable'
import { NavBar } from 'src/components/NavBar'
import { RoomSummary } from 'src/components/RoomSummary'
import type { Theme as ThemeType } from 'src/types'
import type { State } from 'src/types/redux'
import { successBooking } from 'src/ducks/booking'
import Oops from 'src/containers/SecureRoom/components/Oops'

const StyledThanks = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;

  ${media.tabletLarge`
    width: 100%;
  `};
  ${media.desktop`
    width: 60%;
  `};
`

const StyledThanksP = styled(StyledP)`
  width: auto;
  margin: 20px 0;
  padding: 0 40px;
  text-align: justify;
`

const StyledList = styled.ul`
  list-style: none;
  padding: 0 40px;
  margin: 20px 0;
`

const StyledItem = styled.li`
    display: flex
    align-items: flex-start;
    margin-bottom: 20px;
    & > svg {
      min-width: 16px;
      margin: 4px 10px 0 0;
    }
    & > p {
      font-size: 14px;
      color: ${(props: { theme: ThemeType }) => props.theme.darkText};
      margin: 0;
    }
  }
`

const StyledHeader = styled.div`
  padding: 30px;
  font-size: 24px;
  font-weight: 500;
  color: ${(props: { theme: ThemeType }) => props.theme.darkText};
`

type Props = {
  booking: any,
  dispatch: any
}

type MyState = {
  booking: any
}

class ThankYou extends React.PureComponent<Props, MyState> {
  state = {
    booking: {}
  }
  constructor(props) {
    super(props)
    this.state = {
      booking: props.booking
    }
    props.dispatch(successBooking())
  }

  render() {
    if (!this.state.booking.member) {
      return (
        <Oops
          canGoBack={false}
          isThankYou={true}
          title="Oh, hello there!"
          message="You've successfully paid your holding deposit and we've emailed you the confirmation."
        />
      )
    }
    const {
      member: { firstName, lastName, email, phoneNumber, nationality },
      membership: { weeklyPrice, value },
      room: { name, type },
      partner = null,
      guarantor = null,
      bookingDeposit = '',
      location = {},
      adminFee = '',
      moveIn = {},
      isAffordable
    } = this.state.booking
    const finalName = location.ref === 'tccw' ? type : name
    return (
      <StyledFormPage>
        <NewLogo />
        <YellowPattern />
        <GreenPattern />
        <NavBar title={'Room Application'} noTopBar />
        <StyledArticle>
          <StyledThanks>
            <FieldCollection hasShadow>
              <Title>Thank You!</Title>
              <Heading>Your holding deposit has successfully been paid</Heading>
              <StyledP>
                We have sent the payment confirmation to{' '}
                <StyledAnchor href={`mailto:${email}`}>{email}</StyledAnchor>
              </StyledP>
              <Divider moreMargin />
              <RoomSummary name={finalName} size={location.name} noMargin={true} />
              <SubHeading>Personal Information</SubHeading>
              <DetailTable
                object={{
                  firstName,
                  lastName,
                  email,
                  phoneNumber,
                  nationality: nationality.label,
                  salaryCheck: isAffordable ? 'Yes' : 'No'
                }}
                allowed={['firstName', 'lastName', 'email', 'phoneNumber']}
              />
              <Divider />
              {partner && <SubHeading>Your partner&apos;s Information</SubHeading>}
              {partner && (
                <DetailTable
                  object={{
                    name: partner.name,
                    email: partner.email,
                    phoneNumber: partner.phoneNumber
                  }}
                  allowed={['name', 'email', 'phoneNumber']}
                />
              )}
              {partner && <Divider />}

              {guarantor && <SubHeading>Your guarantor&apos;s Information</SubHeading>}
              {guarantor && (
                <DetailTable
                  object={{
                    name: guarantor.name,
                    email: guarantor.email,
                    phoneNumber: guarantor.phoneNumber
                  }}
                  allowed={['name', 'email', 'phoneNumber']}
                />
              )}
              {guarantor && <Divider />}

              <SubHeading>Application Summary</SubHeading>
              <DetailTable
                object={{
                  roomPrice: `£${weeklyPrice} per week`,
                  membershipPeriod: `${value} months`,
                  moveIn: moveIn.label
                }}
                allowed={['roomPrice', 'membershipPeriod', 'moveIn']}
              />
              <Divider />
              <SubHeading>{"You've paid"}</SubHeading>
              <DetailTable
                object={{
                  HoldingDeposit: `£ ${bookingDeposit}`,
                  MembershipJoiningFee: `£ ${adminFee}`
                }}
                allowed={['HoldingDeposit', 'MembershipJoiningFee']}
              />
              <Divider />
              <Flex noMargin>
                <SubHeading>Total paid</SubHeading>
                <SubHeading>{`£ ${parseInt(bookingDeposit, 10) +
                  parseInt(adminFee, 10)}`}</SubHeading>
              </Flex>
              <br />
              <br />
              <Button cssClass="no-print" onClick={() => window.print()} uppercase>
                Print your receipt
              </Button>
            </FieldCollection>
          </StyledThanks>
          <StyledFormInfo>
            <StyledHeader>
              <Title>Next step...</Title>
            </StyledHeader>
            <StyledThanksP moreMargin smaller>
              You will need to provide us with the following documents within 10 days otherwise we
              will not be able to guarantee you your room.
              <br />
              <br />
              In the event that your move-in date is less than 10 days after completing this form,
              you will need to submit the verification documents at least one day before your
              move-in date.
            </StyledThanksP>
            <StyledList>
              <StyledItem>
                <Tick color="#29dc8d" />
                <p>Passport or other ID</p>
              </StyledItem>
              <StyledItem>
                <Tick color="#29dc8d" />
                <p>Visa if applicable</p>
              </StyledItem>
              <StyledItem>
                <Tick color="#29dc8d" />
                <p>Proof of address</p>
              </StyledItem>
              <StyledItem>
                <Tick color="#29dc8d" />
                <p>Proof of annual income</p>
              </StyledItem>
            </StyledList>
            <StyledThanksP moreMargin smaller>
              Once we have verified your documents and you have set up direct debit, we will provide
              you with your contract.
            </StyledThanksP>
            <br />
            {/*<StyledThanksP moreMargin smaller>*/}
            {/*<Button cssClass="no-print" makeDisabled onClick={() => {}} uppercase>*/}
            {/*Start verification now*/}
            {/*</Button>*/}
            {/*</StyledThanksP>*/}
            <br />
            <br />
          </StyledFormInfo>
        </StyledArticle>
      </StyledFormPage>
    )
  }
}

const mapStateToProps = (state: State) => {
  const { booking } = state
  return {
    booking
  }
}

export default connect(mapStateToProps, null)(ThankYou)



// WEBPACK FOOTER //
// ./src/containers/SecureRoom/components/ThankYou/index.js